import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { ArrowDownLeft, ArrowRight, ArrowUpRight, CheckCircle2, Layers, Pencil, RefreshCw, Rocket, Server, Undo2 } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, Input, PageHeader, Skeleton, Switch, Textarea, cn, notifySaved, toast } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic } from "../app/console-context";
import { useChangeSets } from "../features/releases/api";
import { useEnvironments, usePromotionActions, usePromotionMatrix, usePromotions, type EnvironmentView, type PromotionStatus, type PromotionView } from "../features/environments/api";

const STATUS_TONE: Record<PromotionStatus, "outline" | "success" | "info" | "danger" | "warning"> = {
  Sent: "outline", Received: "info", Published: "success", RolledBack: "warning", Failed: "danger", Rejected: "danger",
};
const TILE: Record<string, string> = { sea: "border-seablue-200 bg-seablue-50", camel: "border-camel-200 bg-camel-50", green: "border-success-100 bg-success-50", sand: "border-sand-200 bg-sand-50" };

/** Environments: the promotion path (development → staging → production), promotion of published releases, versions per environment, and rollback here. */
export function EnvironmentsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  useBreadcrumbs([{ label: t("admin:nav.environments") }]);
  useHelpTopic({ title: t("admin:environments.title"), docsAnchor: "environments", body: <><p>{t("admin:environments.help.path")}</p><p>{t("admin:environments.help.signing")}</p><p>{t("admin:environments.help.rollback")}</p></> });
  const envs = useEnvironments();
  const history = usePromotions();
  const matrix = usePromotionMatrix();
  const published = useChangeSets({ status: ["Published"], take: 20 });
  const actions = usePromotionActions();
  const [editing, setEditing] = useState<EnvironmentView | null>(null);
  const [rollingBack, setRollingBack] = useState<PromotionView | null>(null);
  const name = (key: string) => { const e = envs.data?.environments.find((x) => x.key === key); return e ? (locale === "ar" && e.nameAr ? e.nameAr : e.name) : key; };
  const next = envs.data?.next ? name(envs.data.next) : null;
  const promotedIds = useMemo(() => new Set((history.data ?? []).filter((p) => p.direction === "Outbound" && p.status !== "Failed").map((p) => p.changeSetId)), [history.data]);

  // The target decides what happens to a promotion, so outbound rows still awaiting it are asked once when the page opens; a target that
  // cannot be asked is reported with the cause (useSave), not left silent.
  const asked = useRef(new Set<string>());
  useEffect(() => {
    for (const p of history.data ?? []) {
      if (p.direction !== "Outbound" || !p.remotePromotionId || (p.status !== "Sent" && p.status !== "Received") || asked.current.has(p.id)) continue;
      asked.current.add(p.id);
      actions.refresh.mutate(p.id);
    }
  }, [history.data, actions.refresh]);

  const promote = async (id: string) => {
    try {
      const p = await actions.promote.mutateAsync(id);
      if (p.status === "Failed") toast.error(t("admin:environments.promoteFailed", { error: p.error ?? "" }));
      else notifySaved(p, t("admin:environments.promoted", { env: name(p.target) }));
    } catch { /* useSave reported the failure with its cause */ }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t("admin:environments.title")} description={t("admin:environments.subtitle")} />

      <section data-testid="environment-path">
        {envs.isLoading ? <Skeleton className="h-32" /> : (
          <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center">
            {envs.data?.environments.map((e, i) => (
              <div key={e.key} className="flex flex-1 items-center gap-3">
                <Card className={cn("flex-1 border-2", TILE[e.color] ?? TILE.sand, e.isCurrent && "ring-2 ring-aegold-500 ring-offset-2", !e.enabled && "opacity-50")} data-testid={`environment-${e.key}`}>
                  <CardContent className="space-y-1.5 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 font-semibold"><Server className="size-4" /> {locale === "ar" && e.nameAr ? e.nameAr : e.name}</div>
                      <div className="flex items-center gap-1">
                        {e.isCurrent ? <Badge variant="gold" size="sm">{t("admin:environments.thisOne")}</Badge> : null}
                        {can("platform.environments.write") ? <Button size="icon" variant="ghost" aria-label={t("admin:common.edit")} onClick={() => setEditing(e)}><Pencil /></Button> : null}
                      </div>
                    </div>
                    <div className="truncate font-mono text-xs text-muted-foreground" dir="ltr">{e.baseUrl}</div>
                    <div className="text-xs text-muted-foreground">{t("admin:environments.trustKey")}: <code className="font-mono" dir="ltr">{e.trustKeyRef ?? "—"}</code></div>
                  </CardContent>
                </Card>
                {i < (envs.data?.environments.length ?? 0) - 1 ? <ArrowRight className="hidden size-5 shrink-0 text-muted-foreground lg:block rtl:rotate-180" /> : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Rocket className="size-4" /> {t("admin:environments.promoteTitle")}</CardTitle>
            <CardDescription>{next ? t("admin:environments.promoteIntro", { env: next }) : t("admin:environments.lastEnvironment")}</CardDescription>
          </CardHeader>
          <CardContent>
            {published.isLoading ? <Skeleton className="h-32" /> : (published.data ?? []).filter((c) => !c.rollbackOfChangeSetId).length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:environments.noPublished")}</p> : (
              <ul className="divide-y divide-sand-100" data-testid="promotable-releases">
                {(published.data ?? []).filter((c) => !c.rollbackOfChangeSetId).slice(0, 10).map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <Link to={`/releases/${c.id}`} className="block truncate text-sm font-medium hover:underline">{c.title}</Link>
                      <div className="text-xs text-muted-foreground">{t("admin:environments.releaseMeta", { count: c.itemCount, date: c.publishedAt ? formatDateTime(c.publishedAt, locale) : "—" })}</div>
                    </div>
                    {promotedIds.has(c.id) ? <Badge variant="success" size="sm"><CheckCircle2 className="size-3" /> {t("admin:environments.alreadyPromoted")}</Badge>
                      : next && can("platform.environments.promote") ? <Button size="sm" variant="gold" data-testid={`promote-${c.id}`} loading={actions.promote.isPending && actions.promote.variables === c.id} onClick={() => void promote(c.id)}><Rocket /> {t("admin:environments.promoteTo", { env: next })}</Button> : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2"><Layers className="size-4" /> {t("admin:environments.matrixTitle")}</CardTitle>
              <CardDescription>{t("admin:environments.matrixIntro")}</CardDescription>
            </div>
            <Button size="sm" variant="outline" loading={matrix.isFetching} onClick={() => void matrix.refetch()}><RefreshCw /> {t("admin:environments.refresh")}</Button>
          </CardHeader>
          <CardContent>
            {matrix.isLoading ? <Skeleton className="h-32" /> : !matrix.data || matrix.data.rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:environments.matrixEmpty")}</p> : (
              <div className="overflow-x-auto rounded-xl border border-sand-200" data-testid="promotion-matrix">
                <table className="w-full text-sm">
                  <thead className="bg-sand-50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t("admin:environments.definition")}</th>
                      {matrix.data.environments.map((e) => <th key={e.key} className="px-3 py-2 text-center font-medium" title={e.error ?? undefined}>{locale === "ar" && e.nameAr ? e.nameAr : e.name}{e.reachable ? "" : " ⚠"}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sand-100">
                    {matrix.data.rows.map((r) => {
                      const values = matrix.data!.environments.filter((e) => e.reachable).map((e) => r.versions[e.key] ?? (r.present?.[e.key] ? 0 : null));
                      const hashes = matrix.data!.environments.filter((e) => e.reachable).map((e) => r.hashes?.[e.key] ?? null);
                      const aligned = hashes.every((h) => h) ? hashes.every((h) => h === hashes[0]) : values.every((v) => v === values[0]);
                      return (
                        <tr key={`${r.type}:${r.key}`}>
                          <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-[11px] text-muted-foreground">{t(`admin:releases.types.${r.type}`, { defaultValue: r.type })} · <code dir="ltr">{r.key}</code></div></td>
                          {matrix.data!.environments.map((e) => {
                            const v = r.versions[e.key];
                            return <td key={e.key} className="px-3 py-2 text-center">{!e.reachable ? <span className="text-muted-foreground">?</span> : v == null ? (r.present?.[e.key] ? <Badge variant={aligned ? "success" : "outline"} size="sm">{t("admin:environments.present")}</Badge> : <span className="text-muted-foreground">—</span>) : <Badge variant={aligned ? "success" : "outline"} size="sm">v{v}</Badge>}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {matrix.data?.environments.filter((e) => !e.reachable).map((e) => <InlineAlert key={e.key} tone="warning" className="mt-2">{t("admin:environments.unreachable", { env: e.name, error: e.error ?? "" })}</InlineAlert>)}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>{t("admin:environments.historyTitle")}</CardTitle><CardDescription>{t("admin:environments.historyIntro")}</CardDescription></CardHeader>
        <CardContent>
          {history.isLoading ? <Skeleton className="h-40" /> : (history.data ?? []).length === 0 ? <EmptyState icon={Rocket} title={t("admin:environments.noHistory")} description={t("admin:environments.noHistoryBody")} /> : (
            <div className="overflow-x-auto rounded-xl border border-sand-200" data-testid="promotion-history">
              <table className="w-full text-sm">
                <thead className="bg-sand-50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:environments.columns.release")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:environments.columns.path")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:environments.columns.items")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:environments.columns.status")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:environments.columns.by")}</th>
                    <th className="px-3 py-2 text-end font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {history.data!.map((p) => (
                    <tr key={p.id} className="align-top">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 font-medium">
                          {p.direction === "Outbound" ? <ArrowUpRight className="size-3.5 text-aegold-700" /> : <ArrowDownLeft className="size-3.5 text-seablue-700" />}
                          {p.changeSetId ? <Link to={`/releases/${p.changeSetId}`} className="hover:underline">{p.title}</Link> : p.title}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{formatDateTime(p.createdAt, locale)} · <span className="font-mono" dir="ltr">sha256 {p.packageSha256.slice(0, 10)}…</span></div>
                        {p.error ? <div className="mt-0.5 max-w-md text-[11px] text-muted-foreground">{p.error}</div> : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{name(p.source)} <ArrowRight className="inline size-3 rtl:rotate-180" /> {name(p.target)}</td>
                      <td className="px-3 py-2">
                        <div className="flex max-w-xs flex-wrap gap-1">{(p.items ?? []).slice(0, 6).map((i) => <Badge key={`${i.type}:${i.key}`} variant="outline" size="sm">{i.name}{i.version ? ` v${i.version}` : ""}</Badge>)}{(p.items?.length ?? 0) > 6 ? <Badge variant="outline" size="sm">+{p.items!.length - 6}</Badge> : null}</div>
                        {p.evaluations > 0 ? <div className="mt-1 text-[11px] text-muted-foreground">{t("admin:environments.evaluationsCarried", { count: p.evaluations })}</div> : null}
                      </td>
                      <td className="px-3 py-2"><Badge variant={STATUS_TONE[p.status]} size="sm" dot>{t(`admin:environments.status.${p.status}`)}</Badge></td>
                      <td className="px-3 py-2 text-xs">{p.requestedByName}</td>
                      <td className="px-3 py-2 text-end whitespace-nowrap">
                        {p.direction === "Outbound" && p.remotePromotionId ? <Button size="sm" variant="ghost" loading={actions.refresh.isPending && actions.refresh.variables === p.id} onClick={() => actions.refresh.mutate(p.id)}><RefreshCw /> {t("admin:environments.refresh")}</Button> : null}
                        {p.direction === "Inbound" && p.status === "Published" && can("platform.releases.rollback") ? <Button size="sm" variant="outline" data-testid={`rollback-${p.id}`} onClick={() => setRollingBack(p)}><Undo2 /> {t("admin:environments.rollbackHere")}</Button> : null}
                        {p.direction === "Inbound" && p.status === "Received" && p.changeSetId ? <Link to={`/releases/${p.changeSetId}`}><Button size="sm" variant="gold">{t("admin:environments.review")}</Button></Link> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing ? <EditEnvironment env={editing} onClose={() => setEditing(null)} /> : null}
      {rollingBack ? <RollbackDialog promotion={rollingBack} envName={name(rollingBack.target)} onClose={() => setRollingBack(null)} /> : null}
    </div>
  );
}

function EditEnvironment({ env, onClose }: { env: EnvironmentView; onClose: () => void }) {
  const { t } = useTranslation();
  const { save } = usePromotionActions();
  const [form, setForm] = useState({ name: env.name, nameAr: env.nameAr ?? "", baseUrl: env.baseUrl, trustKeyRef: env.trustKeyRef ?? "", order: env.order, enabled: env.enabled });
  const submit = async () => {
    try { const saved = await save.mutateAsync({ key: env.key, input: { ...form, nameAr: form.nameAr || null } }); notifySaved(saved, t("admin:environments.saved")); onClose(); }
    catch { /* useSave reported the failure with its cause; the dialog stays open */ }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("admin:environments.editTitle", { env: env.name })}</DialogTitle><DialogDescription>{t("admin:environments.editIntro")}</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:environments.fields.name")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label={t("admin:environments.fields.nameAr")}><Input dir="rtl" value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} /></Field>
          <Field label={t("admin:environments.fields.baseUrl")} className="sm:col-span-2"><Input dir="ltr" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} /></Field>
          <Field label={t("admin:environments.fields.trustKey")} hint={t("admin:environments.fields.trustKeyHint")} className="sm:col-span-2"><Input dir="ltr" value={form.trustKeyRef} onChange={(e) => setForm({ ...form, trustKeyRef: e.target.value })} /></Field>
          <Field label={t("admin:environments.fields.order")}><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} /></Field>
          <label className="flex items-center gap-2 self-end pb-2 text-sm"><Switch checked={form.enabled} disabled={env.isCurrent} onCheckedChange={(c) => setForm({ ...form, enabled: c })} /> {t("admin:environments.fields.enabled")}</label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" loading={save.isPending} onClick={() => void submit()}>{t("admin:common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RollbackDialog({ promotion, envName, onClose }: { promotion: PromotionView; envName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { rollback } = usePromotionActions();
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { const r = await rollback.mutateAsync({ id: promotion.id, reason }); notifySaved(r, t("admin:environments.rolledBack", { env: envName })); onClose(); }
    catch { /* useSave reported the failure with its cause; the dialog stays open */ }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("admin:environments.rollbackTitle", { env: envName })}</DialogTitle><DialogDescription>{t("admin:environments.rollbackIntro")}</DialogDescription></DialogHeader>
        <Field label={t("admin:environments.reason")}><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rollback-reason" /></Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("admin:common.cancel")}</Button>
          <Button variant="destructive" disabled={!reason.trim()} loading={rollback.isPending} onClick={() => void submit()} data-testid="rollback-confirm"><Undo2 /> {t("admin:environments.rollbackHere")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
